import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { TZDate } from 'react-day-picker';

import { isSelectedFilterPreset } from '@cast/design-system';
import { DateRange, DateTime, TimeRange } from '@cast/shared-types';

import { allFilters } from '../constants/time-range';
import { DateRangeFilter } from '../types/time-range';

import { TimeRangeConstraints } from './types';

dayjs.extend(utc);

export const isValidDateRange = (
  from: DateTime,
  to: DateTime,
  maxDiffInDays = 31
): boolean =>
  from.isValid() &&
  to.isValid() &&
  from.isBefore(to) &&
  to.diff(from, 'day') <= maxDiffInDays;

type GetDateRangeFilterFromValueArgs = {
  value: DateRange;
  filters: readonly DateRangeFilter[];
  allowTimeInput: boolean;
  timezone?: string;
};

export const getDateRangeFilterFromValue = ({
  value,
  filters,
  allowTimeInput,
  timezone = dayjs.tz.guess(),
}: GetDateRangeFilterFromValueArgs): DateRangeFilter | undefined => {
  return filters.find((key) => {
    const filter = allFilters[key];
    return isSelectedFilterPreset({
      value: {
        from: new TZDate(value[0].toISOString(), timezone),
        to: new TZDate(value[1].toISOString(), timezone),
      },
      filter,
      timezone,
      allowTimeInput,
    });
  });
};

export const validateRange = (
  range: TimeRange<string>,
  timezone: string,
  constraints?: TimeRangeConstraints
): TimeRange<string> => {
  const { from, to } = range;
  // Override "from" date and if it violates constraints
  let _from = from;

  const minDate =
    typeof constraints?.minDate === 'function'
      ? constraints.minDate({
          range,
          timezone,
        })
      : constraints?.minDate;

  if (minDate && dayjs.utc(from).isBefore(minDate)) {
    _from = minDate;
  }

  // Override "to" date and if it violates constraints
  let _to = to;

  const maxDate =
    typeof constraints?.maxDate === 'function'
      ? constraints.maxDate({
          range,
          timezone,
        })
      : constraints?.maxDate;

  if (maxDate && dayjs.utc(_to).isAfter(maxDate)) {
    _to = maxDate;
  }

  // Adjust "from" date if the range exceeds maxNumberOfDays
  // Keep "to" date and adjust "from" to select most recent period
  if (constraints?.maxNumberOfDays !== undefined) {
    const daysDiff = dayjs.utc(_to).diff(dayjs.utc(_from), 'day');
    if (daysDiff > constraints.maxNumberOfDays) {
      _from = dayjs
        .utc(_to)
        .subtract(constraints.maxNumberOfDays, 'day')
        .toISOString();
    }
  }

  return { from: _from, to: _to };
};

type GetCorrectRangeArgs = {
  preset?: DateRangeFilter;
  timezone: string;
  allowTimeInput: boolean;
  constraints?: TimeRangeConstraints;
  from?: string;
  to?: string;
  fallbackPreset?: DateRangeFilter;
};

export const determineCorrectDateFromPresetOrRange = ({
  preset,
  timezone,
  allowTimeInput,
  constraints,
  from,
  to,
  fallbackPreset,
}: GetCorrectRangeArgs): TimeRange<string> => {
  // prefer to use user selected preset
  if (preset) {
    const presetFilter = allFilters[preset];
    if (presetFilter) {
      return validateRange(
        presetFilter.date(timezone, allowTimeInput),
        timezone,
        constraints
      );
    }
  }

  // fallback to use user selected date range
  if (from && to && dayjs(from).isValid() && dayjs(to).isValid()) {
    return validateRange({ from, to }, timezone, constraints);
  }

  // fallback to default filter preset
  if (fallbackPreset) {
    const initialPresetFilter = allFilters[fallbackPreset];
    if (initialPresetFilter) {
      return validateRange(
        initialPresetFilter.date(timezone, allowTimeInput),
        timezone,
        constraints
      );
    }
  }
  // the ultimate fallback - this will set currentDateRange to today's date
  return validateRange(
    {
      from: dayjs().tz(timezone).toISOString(),
      to: dayjs().tz(timezone).toISOString(),
    },
    timezone,
    constraints
  );
};
